import type { Address } from "viem"
import { baseSepolia, sepolia } from "viem/chains"
// Setup
export const DETERMINISTIC_DEPLOYER: Address =
  "0x4e59b44847b379578588920ca78fbf26c0b4956c"
export const CREATE2_FACTORY_DEPLOYER_ADDRESS: Address =
  "0x3fAB184622Dc19b6109349B94811493BF2a45362"
export const ENTRYPOINT_V07_ADDRESS: Address =
  "0x0000000071727De22E5E9d8BAf0edAc6f37da032"
export const ENTRY_POINT_SIMULATIONS_ADDRESS: Address =
  "0x74Cb5e4eE81b86e70f9045036a1C5477de69eE87"

// Nexus v1.2.0
export const NEXUS_IMPLEMENTATION_ADDRESS: Address =
  "0x000000004F43C49e93C970E84001853a70923B03"
export const NEXUS_BOOTSTRAP_ADDRESS: Address =
  "0x00000000D3254452a909E4eeD47455Af7E27C289"
export const NEXUS_ACCOUNT_FACTORY_ADDRESS: Address =
  "0x000000001D1D5004a02bAfAb9de2D6CE5b7B13de"
export const MEE_VALIDATOR_ADDRESS: Address =
  "0x00000000d12897DDAdC2044614A9677B191A2d95"
export const COMPOSABLE_MODULE_ADDRESS: Address =
  "0x00000004430bB055dB66eBef6Fe5Ee1DA9668B10"

// Smart Sessions
export const SMART_SESSION_ADDRESS: Address =
  "0x00000000002B0eCfbD0496EE71e01257dA0E37DE"
export const SUDO_POLICY_ADDRESS: Address =
  "0x0000003111cD8e92337C100F22B7A9dbf8DEE301"
export const SPENDING_LIMITS_POLICY_ADDRESS: Address =
  "0x00000088D48cF102A8Cdb0137A9b173f957c6343"
export const UNIVERSAL_ACTION_POLICY_ADDRESS: Address =
  "0x0000006DDA6c463511C4e9B05CFc34C1247fCF1F"
export const TIME_FRAME_POLICY_ADDRESS: Address =
  "0x8177451511dE0577b911C254E9551D981C26dc72"
export const VALUE_LIMIT_POLICY_ADDRESS: Address =
  "0x730DA93267E7E513e932301B47F2ac7D062abC83"

// Ownables
export const OWNABLE_VALIDATOR_ADDRESS: Address =
  "0x2483DA3A338895199E5e538530213157e931Bf06"
export const OWNABLE_EXECUTOR_ADDRESS: Address =
  "0x4Fd8d57b94966982B62e9588C27B4171B55E8354"

// Test contracts
export const MINTABLE_ADDRESS: Address =
  "0x1758f42Af7026fBbB559Dc60EcE0De3ef81f665e"
export const COUNTER_ADDRESS: Address =
  "0x14e4829E655F0b3a1793838dDd47273D5341d416"
export const TOKEN_WITH_PERMIT: Address =
  "0x51fdb803fD49f0f5bd03de0400a8F17dA2Aa6999"
export const MOCK_SIGNATURE_VALIDATOR: Address =
  "0x0d0C730F50a6da2725d4CD4eb91Bc678Bd377F7D"
export const MOCK_CALLEE: Address = "0x29FdD9D9A9f8CD8dCa0F4764bf0F959183DF4139"
export const MOCK_TOKEN: Address = "0x0006be192b4E06770eaa624AE7648DBF9051221c"
export const FREE_MINT_ERC20: Address =
  "0x4a9829524f4879700A8BA8524769F4b2C1CbA264"

export const MEE_ENTRYPOINT: Address =
  "0xE854C84cD68fC434cB3B0042c29235D452cAD977"

export const ALL_ADDRESSES: Record<
  string,
  { address: Address; chainId: number }
> = {
  DETERMINISTIC_DEPLOYER: {
    address: DETERMINISTIC_DEPLOYER,
    chainId: sepolia.id
  },
  ENTRYPOINT_V07_ADDRESS: {
    address: ENTRYPOINT_V07_ADDRESS,
    chainId: sepolia.id
  },
  NEXUS_IMPLEMENTATION_ADDRESS: {
    address: NEXUS_IMPLEMENTATION_ADDRESS,
    chainId: sepolia.id
  },
  NEXUS_ACCOUNT_FACTORY_ADDRESS: {
    address: NEXUS_ACCOUNT_FACTORY_ADDRESS,
    chainId: sepolia.id
  },
  CREATE2_FACTORY_DEPLOYER_ADDRESS: {
    address: CREATE2_FACTORY_DEPLOYER_ADDRESS,
    chainId: sepolia.id
  },
  ENTRY_POINT_SIMULATIONS_ADDRESS: {
    address: ENTRY_POINT_SIMULATIONS_ADDRESS,
    chainId: sepolia.id
  },
  MEE_VALIDATOR_ADDRESS: {
    address: MEE_VALIDATOR_ADDRESS,
    chainId: sepolia.id
  },
  SMART_SESSION_ADDRESS: {
    address: SMART_SESSION_ADDRESS,
    chainId: sepolia.id
  },
  SUDO_POLICY_ADDRESS: { address: SUDO_POLICY_ADDRESS, chainId: sepolia.id },
  SPENDING_LIMITS_POLICY_ADDRESS: {
    address: SPENDING_LIMITS_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  UNIVERSAL_ACTION_POLICY_ADDRESS: {
    address: UNIVERSAL_ACTION_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  TIME_FRAME_POLICY_ADDRESS: {
    address: TIME_FRAME_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  OWNABLE_VALIDATOR_ADDRESS: {
    address: OWNABLE_VALIDATOR_ADDRESS,
    chainId: sepolia.id
  },
  OWNABLE_EXECUTOR_ADDRESS: {
    address: OWNABLE_EXECUTOR_ADDRESS,
    chainId: sepolia.id
  },
  VALUE_LIMIT_POLICY_ADDRESS: {
    address: VALUE_LIMIT_POLICY_ADDRESS,
    chainId: sepolia.id
  },
  MINTABLE_ADDRESS: { address: MINTABLE_ADDRESS, chainId: sepolia.id },
  COMPOSABLE_MODULE_ADDRESS: {
    address: COMPOSABLE_MODULE_ADDRESS,
    chainId: sepolia.id
  },
  // Base sepolia
  MEE_ENTRYPOINT: { address: MEE_ENTRYPOINT, chainId: baseSepolia.id },
  NEXUS_BOOTSTRAP_ADDRESS: {
    address: NEXUS_BOOTSTRAP_ADDRESS,
    chainId: baseSepolia.id
  },
  COUNTER_ADDRESS: { address: COUNTER_ADDRESS, chainId: baseSepolia.id },
  TOKEN_WITH_PERMIT: { address: TOKEN_WITH_PERMIT, chainId: baseSepolia.id },
  MOCK_SIGNATURE_VALIDATOR: {
    address: MOCK_SIGNATURE_VALIDATOR,
    chainId: baseSepolia.id
  },
  MOCK_CALLEE: { address: MOCK_CALLEE, chainId: baseSepolia.id },
  MOCK_TOKEN: { address: MOCK_TOKEN, chainId: baseSepolia.id },
  FREE_MINT_ERC20: { address: FREE_MINT_ERC20, chainId: baseSepolia.id }
}
